import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Compass, Crown, Trophy, ChartBar, Target, Check } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category colors
const CATEGORY_COLORS = {
  'Leadership': '#3B82F6',
  'Vision': '#8B5CF6', 
  'Resilience': '#10B981',
  'Purpose': '#F59E0B',
  'Strategy': '#EF4444',
  'Legacy': '#C4A661'
};

const GrowthPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/devotionals/stats`, {
        headers: getAuthHeader()
      });
      setStats(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center">
        <div className="animate-pulse text-[#6B6560] font-satoshi">Loading progress...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] pb-24">
        <header className="sticky top-0 z-40 bg-[#F5F3EE]/95 backdrop-blur-sm border-b border-[#1A1714]/10">
          <div className="container mx-auto px-5 py-4">
            <h1 className="font-instrument text-2xl text-[#1A1714]">Your Growth</h1>
          </div>
        </header>
        
        <div className="flex flex-col items-center justify-center px-5 py-20">
          <Trophy size={48} className="text-[#B8932B] mb-4" />
          <h2 className="font-instrument text-xl text-[#1A1714] mb-2">Track Your Progress</h2>
          <p className="text-[#6B6560] font-satoshi text-center mb-6">
            Sign in to track your devotional journey and see your growth over time.
          </p>
          <Link to="/login" className="btn-devotional btn-devotional-primary">
            Sign In
          </Link>
        </div>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1714]/10 z-50">
          <div className="flex items-center justify-around py-3">
            <Link to="/devotionals" className="flex flex-col items-center gap-1 text-[#6B6560]">
              <Compass size={24} />
              <span className="text-xs font-satoshi">Devotionals</span>
            </Link>
            <Link to="/devotionals/leaders" className="flex flex-col items-center gap-1 text-[#6B6560]">
              <Crown size={24} />
              <span className="text-xs font-satoshi">Leaders</span>
            </Link>
            <Link to="/devotionals/growth" className="flex flex-col items-center gap-1 text-[#B8932B]">
              <Trophy size={24} weight="fill" />
              <span className="text-xs font-satoshi font-medium">Growth</span>
            </Link>
          </div>
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EE] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F5F3EE]/95 backdrop-blur-sm border-b border-[#1A1714]/10">
        <div className="container mx-auto px-5 py-4">
          <h1 className="font-instrument text-2xl text-[#1A1714]">Your Growth</h1>
          <p className="text-sm text-[#6B6560] font-satoshi">
            Building your legacy, one devotional at a time
          </p>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="px-5 py-6">
        <div className="grid grid-cols-2 gap-4">
          {/* Devotionals Completed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-5 border border-[#1A1714]/10"
          >
            <div className="w-10 h-10 rounded-full bg-[#B8932B]/10 flex items-center justify-center mb-3">
              <Check size={20} className="text-[#B8932B]" weight="bold" />
            </div>
            <p className="text-[#6B6560] font-satoshi text-sm mb-1">Completed</p>
            <p className="font-instrument text-3xl text-[#1A1714]">
              {stats?.completed_count || 0}
              <span className="text-lg text-[#6B6560]">/{stats?.total_devotionals || 12}</span>
            </p>
          </motion.div>

          {/* Mastery Score */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 border border-[#1A1714]/10"
          >
            <div className="w-10 h-10 rounded-full bg-[#B8932B]/10 flex items-center justify-center mb-3">
              <Target size={20} className="text-[#B8932B]" weight="bold" />
            </div>
            <p className="text-[#6B6560] font-satoshi text-sm mb-1">Mastery</p>
            <p className="font-instrument text-3xl text-[#1A1714]">
              {stats?.average_quiz_score || 0}
              <span className="text-lg text-[#6B6560]">%</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Breakdown */}
      <section className="px-5 py-4">
        <h2 className="font-instrument text-lg text-[#1A1714] mb-4">Category Progress</h2>
        
        <div className="bg-white rounded-2xl p-5 border border-[#1A1714]/10 space-y-4">
          {stats?.category_breakdown && Object.entries(stats.category_breakdown).map(([category, data], idx) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: CATEGORY_COLORS[category] }}
                  />
                  <span className="font-satoshi text-sm text-[#1A1714]">{category}</span>
                </div>
                <span className="font-satoshi text-sm text-[#6B6560]">
                  {data.completed}/{data.total}
                </span>
              </div>
              
              <div className="h-2 bg-[#1A1714]/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(data.completed / data.total) * 100}%` }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: CATEGORY_COLORS[category] }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Recent Activity */}
      {stats?.recent_activity && stats.recent_activity.length > 0 && (
        <section className="px-5 py-4">
          <h2 className="font-instrument text-lg text-[#1A1714] mb-4">Recent Activity</h2>
          
          <div className="bg-white rounded-2xl border border-[#1A1714]/10 overflow-hidden">
            {stats.recent_activity.map((activity, idx) => (
              <motion.div
                key={activity.devotional_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.05 }}
                className={`p-4 flex items-center gap-3 ${
                  idx !== stats.recent_activity.length - 1 ? 'border-b border-[#1A1714]/10' : ''
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-[#B8932B]/10 flex items-center justify-center">
                  <Check size={18} className="text-[#B8932B]" weight="bold" />
                </div>
                <div className="flex-1">
                  <p className="font-satoshi text-sm text-[#1A1714]">
                    Completed devotional
                  </p>
                  <p className="font-satoshi text-xs text-[#6B6560]">
                    {activity.completed_at ? new Date(activity.completed_at).toLocaleDateString() : 'Recently'}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Encouragement Section */}
      <section className="px-5 py-6">
        <div className="bg-[#1A1714] rounded-2xl p-6 text-center">
          <h3 className="font-instrument text-xl text-[#E8E4DD] mb-2">
            Keep Building
          </h3>
          <p className="font-satoshi text-[#9A958E] text-sm mb-4">
            Every devotional completed is another step toward becoming the leader God designed you to be.
          </p>
          <Link to="/devotionals" className="btn-devotional btn-devotional-gold">
            Continue Journey
          </Link>
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1714]/10 z-50">
        <div className="flex items-center justify-around py-3">
          <Link 
            to="/devotionals" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Compass size={24} />
            <span className="text-xs font-satoshi">Devotionals</span>
          </Link>
          
          <Link 
            to="/devotionals/leaders" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Crown size={24} />
            <span className="text-xs font-satoshi">Leaders</span>
          </Link>
          
          <Link 
            to="/devotionals/growth" 
            className="flex flex-col items-center gap-1 text-[#B8932B]"
          >
            <Trophy size={24} weight="fill" />
            <span className="text-xs font-satoshi font-medium">Growth</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default GrowthPage;
